import { GARAGE_FLOOR } from '../world/interior';
import { bodySpeed } from './strength';
import { materialAt, type Material } from './materials';
import { AIRCRAFT_CLASSES, WATER_CLASSES, vehicleRange } from '../content/types';
import type { WorldStream } from '../engine/world-stream';
import { RAMP_GRADE } from '../world/roads';
/** a road's speed limit (km/h) when it names none, and off the road — the cameras' too */
export const DEFAULT_LIMIT = 50;
import { forward, pointInVehicle, vehicleCorners, vehicleTop, type Person, type VehicleEntity } from './entities';

export interface DriveInput { throttle: number; steer: number; up: boolean; down: boolean; /** where the pilot looks (aim), radians like `angle`: a hovering helicopter turns its nose there and strafes with the stick */ heading?: number; /** a human is driving: may leave the road and drive into water (and sink) */ player?: boolean; /** handbrake held (Space): hard braking, and the tail comes round at speed */ handbrake?: boolean }
import { feLanding } from '../world/buildings';
export type MoveMode = 'walk' | 'wade' | 'drive' | 'sink' | 'boat' | 'air';
export const SINK_TIME = 2.5;

const PERSON_SPEED = 4.2, SPRINT_MUL = 1.7;
/** (owner, Oct 1: "running with hands empty should be slightly faster") a player's sprint with nothing in the hands — no weapon out, no bag */
export const EMPTY_HANDS_MUL = 1.08;
export const handsEmpty = (p: Person): boolean => !!p.isPlayer && !p.loot && (p.weaponIndex < 0 || !p.weapons.length);
export const JUMP_SPEED = 6, GRAVITY = 13, JUMP_MAX_STEP = 0.25; /** a deck's slope a walker takes in stride (a ramp's rise per tile, with room) */ export const DECK_STEP = 0.6; // jump apex ≈ 1.4 m (cartoon legs)
/** (track 2) how much of gravity a slope costs a car, m/s² per unit grade (9.81 would be a car in neutral; the engine and the tyres cover about half) */
/** (owner, Sep 11) the speed at which a boat driven at the shore runs up it instead of stopping (m/s; ~29 km/h) */
export const BEACH_SPEED = 8;
/** (bug report, Sep 15) the road classes that are asphalt: their `speedMul` below 1 slows the traffic only */
const PAVED_CLASSES = new Set(['lane', 'street', 'avenue', 'highway']);
const GRADE_G = 4.9;
/** (track 2) the steepest grade a car is charged for — raw ground under a road can be far steeper than any road is, until the roads follow the relief */
const GRADE_MAX = 0.2;
/** the tyres' hold, m/s² at grip 1 (a lively road car pulls ~1 g); the power for wheelspin (accel × tuning, m/s²) */
export const TYRE_G = 11, SPIN_ACCEL = 9, SLIDE_K = 0.25;
/**
 * (owner, Sep 8: "tyres should slip the most when turning and accelerating, resulting in a spin") A corner *spends*
 * grip. The tyres only ever had one budget, and what a corner is using cannot also be pulling a slide straight — a
 * friction circle. Before this the recovery was the same mid-corner as in a straight line, and at ordinary speeds it
 * simply beat the slide the steering generated (8.2 m/s² of slide against 10.1 of hold at 15 m/s), so cornering never
 * slipped at all while braking and wheelspin — which set the skid directly, without any sideways speed — did.
 * `CORNER_REF` is the demand of a hard corner at speed, the point at which a corner is using all it can.
 */
export const CORNER_USE = 0.9, CORNER_REF = 45;
/** the cap on sideways speed as a share of forward: a drift, and — with the wheels spinning — enough to come right round */
export const DRIFT_LAT = 0.5, SPIN_LAT = 1.1;
/** how hard a slide past a drift rotates the car (rad/s per m/s of the excess); steering the other way is what stops it */
export const SPIN_YAW = 0.5;
export const CHUTE_FALL = 4.5, CHUTE_DRIFT = 7, FALL_SAFE = 10; // parachute sink/drift speed (m/s); landing faster than FALL_SAFE hurts
/** Railings only stop movers that are on the deck; something already in the water is never fenced in. Set per move by updatePerson/updateVehicle. */
let railsApply = true;
export const RAIL_H = 1.0, RAIL_BREAK_SPEED = 11; // bridge railings: jump over on foot; a car faster than ~40 km/h smashes through; small steps need no jump
/**
 * A car pushes a small thing over from a standing start (owner, Sep 10: "a car should be able to push through/destroy a
 * cactus or small objects on the side of the road from stand still… getting stuck on something that seems small and
 * light and having to reverse or just leave the vehicle there is bad for gameplay"): anything `crushable` — a bin, a
 * hydrant, a bush, a cactus, a small rock — gives way at a walking pace, not at 18 km/h as before. What is *not*
 * crushable (a tree, a post, a wall) still stops the car.
 */
export const PROP_BREAK_SPEED = 0.8;
/** a lamp post comes down only to a car doing this or more (m/s): a steel pole, not a bin */
export const POLE_BREAK_SPEED = 7;
/** a camera mast is a sturdier thing (owner, Sep 11: "a bit tougher than the lamp posts, but still breakable"): ~40 km/h */
export const CAMERA_BREAK_SPEED = 11;
/** the speed at which the pole on this tile gives way (Infinity: no pole) */
function poleBreakSpeed(w: WorldStream, x: number, y: number): number { const k = w.poleKind(x, y); return k === 'camera' ? CAMERA_BREAK_SPEED : k === 'light' ? POLE_BREAK_SPEED : Infinity; }
/** a small thing occupies the middle of its tile, not the whole tile: the car has to reach this close to its centre to touch it */
export const SMALL_PROP_REACH = 0.32;

/** Is this tile blocked for something at altitude `alt`? Ground walkers also respect terrain. */
export function tileBlocked(w: WorldStream, x: number, y: number, alt: number, mode: MoveMode, floor = 0, lift = 0): boolean {
  if (!w.loaded(x, y)) return true;
  const bh = w.blockH(x, y, lift);
  if (mode === 'air') return bh > alt && bh !== w.decorH(x, y); // aircraft only collide with buildings
  if (mode === 'wade' && bh > alt && bh !== w.decorH(x, y)) {
    // a person at a building: the floor plan decides (doors and rooms are open, walls and windows are not)
    const iw = w.interiorWalkable(x, y, floor);
    if (iw !== undefined) return !iw;
  }
  if (bh > alt) {
    // (owner, Sep 10: "the hit boxes of small objects are too large") a crushable thing is small: only the middle of
    // its tile stops a vehicle; a walker still steps round the whole thing, which is how a hydrant reads on foot
    if ((mode === 'drive' || mode === 'sink') && (w.crushable(x, y) || w.pole(x, y))) { const fx = x - Math.floor(x) - 0.5, fy = y - Math.floor(y) - 0.5; if (Math.abs(fx) > SMALL_PROP_REACH || Math.abs(fy) > SMALL_PROP_REACH) return tileBlockedGround(w, x, y, alt, mode, lift); }
    return true;
  }
  return tileBlockedGround(w, x, y, alt, mode, lift);
}

/** the ground under a tile, for whatever is not stopped by a thing standing on it */
function tileBlockedGround(w: WorldStream, x: number, y: number, alt: number, mode: MoveMode, lift = 0): boolean {
  const t = w.terrainAt(x, y)!;
  if (mode === 'walk') return !t.walkable;
  if (mode === 'wade') return (!t.walkable && !t.boatable) || (railsApply && alt < RAIL_H && w.railIntact(x, y)); // people can swim (slowly); railings are chest high
  if (mode === 'boat') return !t.boatable;
  // (bug report, Sep 12: "car stops full stop when driving on this bridge from east") the deck's road counts for a probe as far
  // above the centre as the ramp puts a vehicle's end — the nose of a bus five tiles up the ramp was read as over the water
  if (mode === 'drive') return !t.drivable && !t.walkable && !w.roadAt(x, y, lift);
  if (mode === 'sink') return (!t.drivable && !t.walkable && !t.boatable && !w.roadAt(x, y, lift)) || (railsApply && w.railIntact(x, y)); // a human driver may end up in the lake — through the railing if fast enough (updateVehicle)
  return false; // aircraft on the ground roll over anything
}

/**
 * A grazing contact should not stop the car dead: try the move with the nose turned a little away
 * from the obstacle (either way), then shorter moves. Applies the first pose that fits.
 */
function slipAlong(w: WorldStream, v: VehicleEntity, dx: number, dy: number, angle: number, ok: (x: number, y: number, a: number) => boolean): boolean {
  void w;
  for (const f of [1, 0.6, 0.3]) for (const da of [0.07, -0.07, 0.15, -0.15]) {
    const a = angle + da * Math.sign(v.speed || 1);
    if (ok(v.x + dx * f, v.y + dy * f, a)) { v.x += dx * f; v.y += dy * f; v.angle = a; return true; }
  }
  return false;
}

/** Corners, centre, nose, tail and the front edge of a vehicle at a pose. */
/** what the car ran into at this pose: the first blocked probe point that names a material (docs/DESIGN_CRASH_SFX.md) */
function hitMaterial(w: WorldStream, v: VehicleEntity, x: number, y: number, angle: number): Material | undefined {
  const mode = vehicleMode(v) === 'boat' ? 'sink' : 'drive';
  for (const [px, py] of probePoints(v, x, y, angle)) { if (!tileBlocked(w, px, py, v.alt, mode)) continue; const m = materialAt(w, px, py); if (m) return m; }
  return undefined;
}
function probePoints(v: VehicleEntity, x: number, y: number, angle: number): [number, number][] {
  const probe = { ...v, x, y, angle };
  const [fx, fy] = forward(angle);
  const hl = v.def.size[1] / 2;
  const pts: [number, number][] = [...vehicleCorners(probe), [x, y], [x + fx * hl, y + fy * hl], [x - fx * hl, y - fy * hl]];
  const [c0, c1] = vehicleCorners(probe);
  for (let t = 0.25; t < 1; t += 0.25) pts.push([c0[0] + (c1[0] - c0[0]) * t, c0[1] + (c1[1] - c0[1]) * t]);
  return pts;
}

/** If everything blocking the vehicle at this pose is breakable (low props/bushes, and railings when `rails`), flatten it and return true. */
/** …returning the tallest thing that gave way (metres; a railing counts as RAIL_H), or 0 when nothing did or something solid stood in the way. */
function smashThrough(w: WorldStream, v: VehicleEntity, x: number, y: number, angle: number, rails: boolean): number {
  const pts = probePoints(v, x, y, angle);
  const hits: [number, number][] = [];
  let tallest = 0;
  for (const [px, py] of pts) {
    if (!tileBlocked(w, px, py, 0, 'sink')) continue;
    if (w.crushable(px, py) || Math.abs(v.speed) > poleBreakSpeed(w, px, py)) { hits.push([px, py]); tallest = Math.max(tallest, w.decorH(px, py)); continue; }
    if (rails && w.railIntact(px, py)) { hits.push([px, py]); tallest = Math.max(tallest, RAIL_H); continue; }
    return 0; // something solid too (a tree, a building, the shore)
  }
  if (!hits.length) return 0;
  for (const [px, py] of hits) w.breakTile(px, py);
  return tallest;
}

/** A boat under a bridge deck (its centre): out of sight for everyone but its crew. */
export function underBridge(w: WorldStream, v: VehicleEntity): boolean {
  if (vehicleMode(v) !== 'boat') return false;
  const t = w.terrainAt(v.x, v.y);
  return !!t && !t.walkable && !!w.roadAt(v.x, v.y);
}
/** Any part of a boat under a deck (the hull's corners and centre): the deck hides that part and the traffic above cannot touch it (owner: a boat halfway under was hit by cars). */
export function underBridgeAny(w: WorldStream, v: VehicleEntity): boolean {
  if (vehicleMode(v) !== 'boat') return false;
  // sample the hull at ≤1-tile steps along its length on the centreline and both sides (a deck is often a single tile wide)
  const [fx, fy] = forward(v.angle), hw = v.def.size[0] / 2 - 0.1, hl = v.def.size[1] / 2 - 0.1, n = Math.max(1, Math.ceil(hl * 2));
  for (let i = 0; i <= n; i++) {
    const a = -hl + (2 * hl * i) / n, cx = v.x + fx * a, cy = v.y + fy * a;
    for (const side of [-hw, 0, hw]) { const x = cx - fy * side, y = cy + fx * side; const t = w.terrainAt(x, y); if (t && !t.walkable && w.roadAt(x, y)) return true; }
  }
  return false;
}

/** Deep water at this point (not a road/bridge)? */
export function isWater(w: WorldStream, x: number, y: number): boolean {
  const t = w.terrainAt(x, y);
  return !!t && !t.walkable && !!t.boatable && !w.roadAt(x, y);
}

/** How many blocked tiles a circle overlaps (0 = free). */
function circleBlockedCount(w: WorldStream, x: number, y: number, r: number, alt: number, mode: MoveMode, floor = 0): number {
  const x0 = Math.floor(x - r), x1 = Math.floor(x + r), y0 = Math.floor(y - r), y1 = Math.floor(y + r);
  let n = 0;
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
    if (!tileBlocked(w, tx, ty, alt, mode, floor)) continue;
    // circle vs the tile AABB — or, for a person at a thin interior wall, vs the wall's slabs
    const rects = mode === 'wade' && w.blockH(tx, ty) > alt ? w.wallRects(tx + 0.5, ty + 0.5, floor) : undefined;
    for (const [ax, ay, bx, by] of rects ?? [[tx, ty, tx + 1, ty + 1]]) {
      const cx = Math.max(ax, Math.min(x, bx)), cy = Math.max(ay, Math.min(y, by));
      if ((cx - x) ** 2 + (cy - y) ** 2 < r * r) { n++; break; }
    }
  }
  return n;
}

/** Move a circle with axis-separated sliding. Returns true if it collided. `extra` adds dynamic obstacles. */
export function moveCircle(w: WorldStream, e: { x: number; y: number }, dx: number, dy: number, r: number, alt: number, mode: MoveMode, extra?: (x: number, y: number) => boolean, floor = 0): boolean {
  let hit = false;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / (r * 0.8)));
  for (let i = 0; i < steps; i++) {
    // someone already overlapping a blocked tile (dropped on a tree, pushed into a wall) may move
    // as long as the move does not make it worse — otherwise they could never get free
    const cur = circleBlockedCount(w, e.x, e.y, r, alt, mode, floor);
    const free = (x: number, y: number): boolean => circleBlockedCount(w, x, y, r, alt, mode, floor) <= cur && !extra?.(x, y);
    const nx = e.x + dx / steps;
    if (free(nx, e.y)) e.x = nx; else { hit = true; if ((mode === 'walk' || mode === 'wade') && Math.abs(dx) > 2 * Math.abs(dy)) slide(nx, 'x'); }
    const ny = e.y + dy / steps;
    if (free(e.x, ny)) e.y = ny; else { hit = true; if ((mode === 'walk' || mode === 'wade') && Math.abs(dy) > 2 * Math.abs(dx)) slide(ny, 'y'); }
    // (owner, Sep 29: "walking through doors is hard sometimes — the player will sometimes get stuck before the door or after it for no
    // reason") a door is one tile and a walker 0.7 m across: off its middle, the body caught the jamb and the move stopped dead. A step
    // straight into an edge now looks up to 0.8 m either side (half a door and a body): where the way on is clear there, the walker slides that way (at the
    // step's own length), round the jamb and through
    function slide(to: number, axis: 'x' | 'y'): void {
      const len = Math.abs(axis === 'x' ? dx : dy) / steps;
      for (let k = 0.05; k <= 0.8 + 1e-9; k += 0.05) for (const sg of [1, -1]) {
        const ok = axis === 'x' ? free(to, e.y + sg * k) && free(e.x, e.y + sg * Math.min(k, len)) : free(e.x + sg * k, to) && free(e.x + sg * Math.min(k, len), e.y);
        if (!ok) continue;
        if (axis === 'x') e.y += sg * Math.min(k, len); else e.x += sg * Math.min(k, len);
        return;
      }
    }
  }
  return hit;
}

/** Height of whatever a walker at (x, y) would stand on: low props, or the roof of a vehicle. */
export function walkFloor(w: WorldStream, x: number, y: number, r: number, z: number, vehicles: readonly VehicleEntity[]): number {
  // an overpass deck is a floor for whoever is (nearly) up on it
  const dh = w.deckH(x, y);
  // (bug report, Sep 13: "I cant walk into the ramp to the east of me") a ramp rises ~0.4 m a tile: a walker takes its slope, which the jump's step (0.25 m) never allowed — they stood on the ground inside its first tiles and the embankment stopped them
  if (dh > 0 && dh <= z + DECK_STEP) { let floor = dh; for (const v of vehicles) { if (Math.abs(v.alt - dh) > 1 || (v.x - x) ** 2 + (v.y - y) ** 2 > 400) continue; const top = v.alt + vehicleTop(v); if (top > floor && top <= z + JUMP_MAX_STEP && pointInVehicle(v, x, y, r * 0.5)) floor = top; } return floor; }
  let floor = 0;
  const x0 = Math.floor(x - r), x1 = Math.floor(x + r), y0 = Math.floor(y - r), y1 = Math.floor(y + r);
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
    const bh = w.blockH(tx, ty);
    if (bh > floor && bh <= z + JUMP_MAX_STEP) floor = bh;
  }
  for (const v of vehicles) {
    if (v.alt > 0.5 || v.wet > 0 || (v.x - x) ** 2 + (v.y - y) ** 2 > 400) continue;
    const top = vehicleTop(v);
    if (top > floor && top <= z + JUMP_MAX_STEP && pointInVehicle(v, x, y, r * 0.5)) floor = top;
  }
  return floor;
}

/** Returns fall damage taken on landing this step (0 most of the time). */
/** a door that will not open for this person: the move that would carry them through it is refused at the threshold (owner: no peeking a
 *  step inside a locked house) — the game answers with its lock rules, the client with what it knows */
export type LockedDoor = (key: string) => boolean;
/** (owner, Sep 10/13: the garage is the floor below, entered by its own door on the street wall) a walker on the street stepping onto a
 *  tile that is a garage door of a floor-below they may use (`mine(key)`) goes down to that floor first, so the step itself lands
 *  on a door cell and not on the ground floor's wall. Shared by the server and the client's prediction. */
export function garageDoorStep(w: WorldStream, p: Person, mx: number, my: number, mine: (key: string) => boolean): boolean {
  if (p.inside || p.floor !== 0 || p.vehicle || (!mx && !my)) return false;
  const n = Math.hypot(mx, my) || 1, tx = Math.floor(p.x + (mx / n) * 0.9), ty = Math.floor(p.y + (my / n) * 0.9);
  const b = w.buildingAt(tx + 0.5, ty + 0.5); if (!b) return false;
  const key = `${b.x},${b.y}`; if (!mine(key)) return false;
  const plan = w.planAt(tx + 0.5, ty + 0.5, GARAGE_FLOOR); if (!plan || !plan.doors.some(([dx, dy]) => dx === tx && dy === ty)) return false;
  p.floor = GARAGE_FLOOR; p.inside = key;
  return true;
}

export function updatePerson(w: WorldStream, p: Person, mx: number, my: number, sprint: boolean, dt: number, jump = false, vehicles: readonly VehicleEntity[] = [], lockedDoor?: LockedDoor): number {
  if (p.vehicle || p.dead) return 0;
  w.setMover(p.x, p.y, p.z);
  try { return updatePersonInner(w, p, mx, my, sprint, dt, jump, vehicles, lockedDoor); } finally { w.setMover(0, 0, 0); }
}

/** (owner, Sep 29) the fire door of landing `level` of building `key` — always locked from the escape, unless picked */
export function fireDoorKey(key: string, level: number): string { return `${key}#fe${level}`; }
/** (owner, Sep 29: "fire escapes outside tall buildings … the doors to floors from the fire escape should always be locked and need a lock
 *  pick") a walker on the iron: along the strip at the landing's height, no falling; pushing into the wall at the fire door goes in —
 *  when it is open (picked) — onto that floor. Up and down are the climb keys (game.ts useStairs). */
function escapeMove(w: WorldStream, p: Person, mx: number, my: number, dt: number, lockedDoor?: LockedDoor): number {
  const [kx, ky] = p.esc!.key.split(',').map(Number), b = w.buildingAt(kx + 0.5, ky + 0.5), fe = b?.fe;
  if (!b || !fe || b.ruined) { p.esc = undefined; return 0; }
  const level = p.esc!.level, sp = PERSON_SPEED * 0.8, m = p.r * 0.6;
  p.z = level * (b.def.storyHeight ?? 3); p.vz = 0; p.inside = undefined; p.floor = 0;
  p.x = Math.max(fe.x + m, Math.min(fe.x + fe.w - m, p.x + mx * sp * dt)); p.y = Math.max(fe.y + m, Math.min(fe.y + fe.h - m, p.y + my * sp * dt));
  // into the wall at the landing: the fire door
  const [lx, ly] = feLanding(fe), into = fe.wall === 0 ? my > 0.5 : fe.wall === 1 ? mx < -0.5 : fe.wall === 2 ? my < -0.5 : mx > 0.5;
  p.bumpedDoor = undefined;
  if (into && Math.floor(p.x) === lx && Math.floor(p.y) === ly && level >= 1 && level < b.def.stories) {
    const dk = fireDoorKey(p.esc!.key, level);
    if (lockedDoor?.(dk)) p.bumpedDoor = dk;
    else { const [dx, dy] = fe.door, ix = fe.wall === 0 ? 0 : fe.wall === 1 ? -0.9 : fe.wall === 2 ? 0 : 0.9, iy = fe.wall === 0 ? 0.9 : fe.wall === 2 ? -0.9 : 0; p.x = dx + 0.5 + ix; p.y = dy + 0.5 + iy; p.z = 0; p.inside = p.esc!.key; p.floor = level; p.esc = undefined; }
  }
  return 0;
}
function updatePersonInner(w: WorldStream, p: Person, mx: number, my: number, sprint: boolean, dt: number, jump: boolean, vehicles: readonly VehicleEntity[], lockedDoor?: LockedDoor): number {
  if (p.esc) return escapeMove(w, p, mx, my, dt, lockedDoor);
  // high in the air (bailed out of an aircraft): parachute drifts, free fall barely steers — high above the *floor*,
  // not above the sea: on a five-metre viaduct you are standing, not falling (owner, Sep 11: "I can't jump on this bridge")
  if (p.z - walkFloor(w, p.x, p.y, p.r, p.z, vehicles) > 2.5) {
    railsApply = false;
    const drift = p.chute ? CHUTE_DRIFT : 2;
    moveCircle(w, p, mx * drift * dt, my * drift * dt, p.r, p.z + JUMP_MAX_STEP, 'wade');
    p.vz = p.chute ? Math.max(-CHUTE_FALL, p.vz - GRAVITY * dt) : p.vz - GRAVITY * dt;
    p.z += p.vz * dt;
    const floor = walkFloor(w, p.x, p.y, p.r, p.z, vehicles);
    if (p.z <= floor) { p.z = floor; return land(p); }
    return 0;
  }
  const t = w.terrainAt(p.x, p.y);
  const road = w.roadAt(p.x, p.y);
  const mul = road ? 1 : t?.speedMul ?? 1;
  const floor0 = walkFloor(w, p.x, p.y, p.r, p.z, vehicles);
  const grounded = p.z <= floor0 + 0.02;
  const swimming = p.z <= 0.02 && isWater(w, p.x, p.y);
  railsApply = !swimming;
  if (jump && grounded && !swimming && !p.inside) p.vz = JUMP_SPEED; // no jumping indoors (ceilings)
  const sp = (swimming ? PERSON_SPEED * (p.dive ? 0.3 : 0.45) : PERSON_SPEED * (sprint ? SPRINT_MUL * (handsEmpty(p) ? EMPTY_HANDS_MUL : 1) : 1) * (grounded && floor0 === 0 ? Math.max(0.4, mul) : 1)) * bodySpeed(p.look) * (swimming ? 1 : p.legs ?? 1); // (the slim build runs a little faster, the broad one a little slower — strength.ts)
  // vehicles are solid to walkers unless they are already above the roof (then they are the floor)
  const alt = p.z + JUMP_MAX_STEP;
  const solidVehicle = (x: number, y: number): boolean => {
    for (const v of vehicles) {
      if (Math.abs(v.alt - Math.max(0, p.z)) > 1.5 || v.wet > 0 || vehicleTop(v) <= alt - v.alt || (v.x - x) ** 2 + (v.y - y) ** 2 > 400) continue;
      if (pointInVehicle(v, x, y, p.r * 0.8) && !pointInVehicle(v, p.x, p.y, p.r * 0.8)) return true; // already inside one (pushed, dropped): let them walk out
    }
    return false;
  };
  const bx = p.x, by = p.y, wasInside = p.inside;
  moveCircle(w, p, mx * sp * dt, my * sp * dt, p.r, alt, 'wade', solidVehicle, p.floor);
  // inside a building? (walked through a door) — not when standing on one's roof (the floor is the building itself)
  const onRoof = floor0 > 2.5 && grounded && w.deckH(p.x, p.y) <= 0;
  let pl = onRoof ? undefined : w.planAt(p.x, p.y, p.floor);
  p.bumpedDoor = undefined;
  if (pl && lockedDoor && `${pl.x},${pl.y}` !== wasInside && lockedDoor(`${pl.x},${pl.y}`)) { p.bumpedDoor = `${pl.x},${pl.y}`; p.x = bx; p.y = by; pl = w.planAt(p.x, p.y, p.floor); } // the threshold: stay on the step
  // (owner, Sep 26: "when you go only half way through a door … that counts as opening the shop menu") the doorway is a threshold, not
  // a room: whoever comes in from outside is inside once past the door tile, and whoever goes out stays inside until past it the other
  // way — half in the door, nothing flips (the shop's list, the interior view) and nothing flips back and forth
  if (pl && wasInside !== `${pl.x},${pl.y}` && pl.doors.some(([dx, dy]) => dx === Math.floor(p.x) && dy === Math.floor(p.y))) pl = undefined;
  // (owner, Sep 29) out of an upper floor's fire door: onto its landing of the escape (anywhere else an upper floor has no way out)
  if (!pl && wasInside && p.floor >= 1) {
    const [kx, ky] = wasInside.split(',').map(Number), ob = w.buildingAt(kx + 0.5, ky + 0.5), fe = ob?.fe;
    if (ob && fe && p.x >= fe.x && p.x < fe.x + fe.w && p.y >= fe.y && p.y < fe.y + fe.h) { p.esc = { key: wasInside, level: p.floor }; p.inside = undefined; p.floor = 0; p.z = p.esc.level * (ob.def.storyHeight ?? 3); p.vz = 0; return 0; }
    p.x = bx; p.y = by; pl = w.planAt(p.x, p.y, p.floor);
  }
  p.inside = pl ? `${pl.x},${pl.y}` : undefined;
  // vertical
  const floor = walkFloor(w, p.x, p.y, p.r, p.z, vehicles);
  if (p.z > floor || p.vz > 0) { p.vz -= GRAVITY * dt; p.z += p.vz * dt; }
  if (p.z <= floor) { p.z = floor; return land(p); }
  return 0;
}

function land(p: Person): number {
  const v = -p.vz;
  p.vz = 0;
  if (p.chute) { p.chute = false; return 0; }
  return v > FALL_SAFE ? (v - FALL_SAFE) * 9 : 0;
}

export function vehicleMode(v: VehicleEntity): 'drive' | 'boat' | 'air' {
  if (AIRCRAFT_CLASSES.has(v.def.class)) return 'air';
  if (WATER_CLASSES.has(v.def.class)) return 'boat';
  return 'drive';
}

export function vehicleBlocked(w: WorldStream, v: VehicleEntity, x: number, y: number, angle: number, mode: MoveMode): boolean {
  return blockedPoints(w, v, x, y, angle, mode, 1) > 0;
}

/** How many of the vehicle's probe points (corners, centre, nose, tail) sit in blocked tiles at a pose; stops counting at `max`. */
export function blockedPoints(w: WorldStream, v: VehicleEntity, x: number, y: number, angle: number, mode: MoveMode, max = 7): number {
  const probe = { ...v, x, y, angle };
  const alt = Math.max(0, v.alt); // a sinking car is still at ground level for blocking purposes
  let n = 0;
  // on a ramp the ends of a vehicle are above its centre: a probe that far out may be that much higher and still be on the deck
  const hl = v.def.size[1] / 2, lift = RAMP_GRADE * Math.hypot(v.def.size[0] / 2, hl), liftEnd = RAMP_GRADE * hl;
  for (const [cx, cy] of vehicleCorners(probe)) { if (!w.loaded(cx, cy)) return 99; /* the unstreamed world is a hard wall, never 'tolerated' */ if (tileBlocked(w, cx, cy, alt, mode, 0, lift) && ++n >= max) return n; }
  if (tileBlocked(w, x, y, alt, mode) && ++n >= max) return n;
  // long vehicles: mid-points of the sides
  const [fx, fy] = forward(angle);
  if (tileBlocked(w, x + fx * hl, y + fy * hl, alt, mode, 0, liftEnd) && ++n >= max) return n;
  if (tileBlocked(w, x - fx * hl, y - fy * hl, alt, mode, 0, liftEnd) && ++n >= max) return n;
  return n;
}

/** Arcade vehicle model. Returns collision speed (m/s) if it hit something this step, else 0. */
export function updateVehicle(w: WorldStream, v: VehicleEntity, inp: DriveInput | undefined, dt: number): number {
  const st = v.def.stats;
  const mode = vehicleMode(v);
  const airborne = mode === 'air' && v.alt > 0.01;
  if (mode === 'air') w.setMover(0, 0, 0); else w.setMover(v.x, v.y, v.alt); // road/blocking queries answer for the deck the car is on (overpasses)
  // land vehicles in deep water: engine dead, sinking; after SINK_TIME they are a wreck on the bottom
  if (mode === 'drive' && !airborne && (isWater(w, v.x, v.y) || (v.wrecked && v.wet > 0))) {
    v.wet += dt;
    v.alt = -Math.min(1.5, v.wet * 0.6);
    v.speed *= Math.exp(-dt * 8); // dead engine, water drag: it stops where it went in
    if (v.wet > SINK_TIME && !v.wrecked) { v.wrecked = true; v.hp = 0; }
  } else if (v.wet > 0) { v.wet = 0; if (v.alt < 0) v.alt = 0; } // pushed back onto the beach before it went under: dries out — (Sep 25) and a car out of the water at any height: only one below the waterline dried, so a car lifted out wet (a teleport, a server correction) stayed "sinking" — engine dead, and the deck no longer carried it: stuck at 4.8 m over a 4.3 m ramp at (468, -407)
  const sinking = v.wet > 0;
  railsApply = !sinking && !isWater(w, v.x, v.y);
  // (owner, Sep 11) a boat driven fast enough at the shore runs up it and slides — and there it stays, an engine that pulls no
  // water, until something pushes it (collide.ts moves it like any hull). Beached = the hull's centre is on ground a boat
  // cannot float on; running up needs a human at the helm and BEACH_SPEED, so traffic never strands itself
  const under = mode === 'boat' ? w.terrainAt(v.x, v.y) : undefined;
  const beached = mode === 'boat' && !sinking && !!under && !under.boatable; // (a terrain without the key is land: sand says nothing about boats)
  const blockMode: MoveMode = mode === 'drive' && (inp?.player || sinking) ? 'sink' : mode === 'boat' && (beached || (inp?.player && Math.abs(v.speed) >= BEACH_SPEED)) ? 'sink' : mode;
  // fuel: only human drivers burn it (NPC traffic is scenery); an empty tank means coasting
  if (inp?.player && !v.wrecked && v.fuel > 0) {
    const cruise = Math.min(1, Math.abs(v.speed) / st.maxSpeed);
    v.fuel = Math.max(0, v.fuel - (dt / (vehicleRange(v.def) * 60)) * (0.1 + 0.9 * cruise) * (1 + 0.4 * Math.abs(inp.throttle)));
  }
  const dead = v.wrecked || sinking || (inp?.player && v.fuel <= 0);
  const handbrake = !!inp?.handbrake && !airborne && mode !== 'air' && mode !== 'boat';
  const throttle = dead || handbrake || beached ? 0 : inp?.throttle ?? 0;
  const steer = v.wrecked || sinking || beached ? 0 : inp?.steer ?? 0;

  // surface factor
  let surface = 1;
  if (!airborne) {
    const road = w.roadAt(v.x, v.y), t = w.terrainAt(v.x, v.y);
    if (mode === 'boat') surface = t?.boatable || beached ? 1 : 0.08; // (a beached hull keeps its way: the slide below is what stops it, not the cap)
    else if (road) surface = inp?.player && road.speedMul < 1 && PAVED_CLASSES.has(road.class) ? 1 : road.speedMul; // (bug report, Sep 15: "on this road the car still drives slower than on bigger roads although this is asphalt") a lane's and a street's lower factor is the traffic's slower pace (the driver rules, Sep 13), not the asphalt's: a human driver keeps full speed; dirt and gravel still hold everyone back
    else if (t) surface = t.drivable ? t.speedMul : 0.12;
    if (mode === 'air') surface = Math.max(surface, 0.9); // runways not modelled yet: fields are fine
  }
  const eng = v.mods?.eng ?? 1 + 0.15 * (v.mods?.engine ?? 0); // tuning
  const maxF = st.maxSpeed * eng * surface, maxR = (st.reverseSpeed ?? 5) * surface;
  // wet roads: brakes bite less and the car turns wider at speed (rain from weather.ts; only on the road, mud is mud anyway).
  // Human drivers only — NPC traffic just drives slower in the rain (ai.ts); its dead-end and queue logic assumes dry brakes
  const slip = inp?.player && !airborne && mode === 'drive' && w.wet > 0 && w.roadAt(v.x, v.y) ? w.wetAt(v.x, v.y) : 0;

  // (track 2) the ground's grade along the heading, whatever the pedals do: gravity's share the engine does not cover — a 10 %
  // climb takes ~0.5 m/s² off, and the descent gives it back. Ground vehicles only; the water and the air have their own physics.
  // A car that is *moving* with a driver feels it; a car at rest holds (its brakes, its gear) — else a parked car, or an NPC
  // waiting at a shore junction, would roll away downhill, and a car starting uphill from a standstill could never get going.
  if (inp && !airborne && Math.abs(v.speed) > 0.3 && v.def.class !== 'boat' && v.def.class !== 'plane' && v.def.class !== 'helicopter') { const grade = Math.max(-GRADE_MAX, Math.min(GRADE_MAX, w.gradeAlong?.(v.x, v.y, v.angle) ?? 0)); if (grade) v.speed -= GRADE_G * grade * dt; }
  // (owner, Sep 24: "adaptive cruise control: your max speed would be the speed limit, to avoid getting tickets") the driver's
  // limiter: the gas takes a car on the road up to the limit of the road it is on and no further, and a car over it (a slower road
  // ahead, a hill) eases down to it — the client's prediction runs this same code, so the car does not jump back
  const limiter = inp?.player && v.cruise && mode === 'drive' && !airborne ? (w.roadAt(v.x, v.y)?.limit ?? DEFAULT_LIMIT) / 3.6 : Infinity;
  if (throttle > 0) {
    if (v.speed < 0) v.speed = Math.min(0, v.speed + st.brake * dt);
    else v.speed = Math.min(Math.max(Math.min(maxF, limiter), v.speed), v.speed + st.accel * eng * throttle * dt);
  } else if (throttle < 0) {
    if (v.speed > 0) v.speed = Math.max(0, v.speed - st.brake * (1 - 0.4 * slip) * -throttle * dt);
    else if (!airborne) v.speed = Math.max(-maxR, v.speed - st.accel * 0.6 * -throttle * dt);
  } else if (handbrake) {
    v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), st.brake * 1.3 * (1 - 0.5 * slip) * dt); // locked wheels
  } else {
    const drag = beached ? 0 : airborne ? 0.5 + Math.abs(v.speed) * 0.02 : 1.5 + Math.abs(v.speed) * 0.12; // (a beached hull has its own slide below)
    v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), drag * dt);
  }
  if (v.speed > maxF) v.speed -= Math.min(v.speed - maxF, (v.speed - maxF) * 2.5 * dt + 1 * dt);
  if (v.speed > limiter && throttle >= 0) v.speed = Math.max(limiter, v.speed - st.brake * 0.5 * dt);
  if (beached) v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), (Math.abs(v.speed) * 0.3 + 1.2) * dt); // (a hull on sand slides by its speed — ~10 m from 9 m/s, ~20 from 15, ~35 from a speedboat's 25: the owner had it at twice this and said half)
  if (v.speed < -maxR) v.speed = Math.min(-maxR, v.speed + 25 * dt); // shoved backwards by a collision: settle quickly

  // altitude
  if (mode === 'air') {
    const fl = v.def.flight!;
    const wasAirborne = v.alt > 0.5;
    const canClimb = v.def.class === 'helicopter' || v.speed >= fl.stallSpeed;
    if (inp?.up && canClimb && !v.wrecked) v.alt = Math.min(fl.maxAltitude, v.alt + fl.climbRate * dt);
    if (inp?.down || v.wrecked) v.alt = Math.max(0, v.alt - fl.climbRate * (v.wrecked ? 2 : 1) * dt);
    if (v.def.class === 'plane' && v.alt > 0 && v.speed < fl.stallSpeed) v.alt = Math.max(0, v.alt - fl.climbRate * 1.5 * dt);
    // touching down: only flat open ground is a landing; trees, buildings, water or a steep descent are a crash
    if (wasAirborne && v.alt <= 0.5 && !v.wrecked) {
      let bad = false;
      for (const [cx, cy] of [...vehicleCorners(v), [v.x, v.y] as [number, number]]) {
        const t = w.terrainAt(cx, cy);
        if (!t || w.blockH(cx, cy) > 0 || (!t.walkable && !t.buildable)) { bad = true; break; }
      }
      if (bad) { v.alt = 0; v.hp = 0; v.wrecked = true; v.bumped = 0.5; return Math.max(8, Math.abs(v.speed)); }
    }
  }

  // a helicopter in the air under a human (owner: fly left, right and back without turning): the nose follows the aim,
  // throttle is fore/aft speed, the stick's sideways is a strafe (`lat`, m/s); both bleed off when let go
  const hover = airborne && v.def.class === 'helicopter' && !!inp?.player && !v.wrecked;
  // …but only while something aims the nose (the mouse, the aim stick). With no aim — a phone's split scheme has no aim stick,
  // and the stick one's is idle until pushed — the stick yaws the nose like any vehicle, or the helicopter could never turn
  // (owner, Sep 9: "helicopter flying controls do not work as intended")
  const strafe = hover && inp?.heading !== undefined;
  if (hover) {
    const latMax = maxF * 0.6;
    if (strafe) v.lat = (v.lat ?? 0) + steer * st.accel * dt;
    if (!steer || !strafe) v.lat = (v.lat ?? 0) - Math.sign(v.lat ?? 0) * Math.min(Math.abs(v.lat ?? 0), (st.brake * 0.5 + Math.abs(v.lat ?? 0) * 0.3) * dt);
    v.lat = Math.max(-latMax, Math.min(latMax, v.lat ?? 0));
    if (throttle < 0 && v.speed <= 0) v.speed = Math.max(-latMax, v.speed + st.accel * throttle * dt); // backwards: as fast as sideways
    if (strafe) { let d = inp!.heading! - v.angle; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2; v.angle += Math.sign(d) * Math.min(Math.abs(d), st.turnRate * 1.5 * dt); }
  } else if (v.lat && !(inp?.player && mode === 'drive')) v.lat = 0;
  // steering
  const spdFactor = airborne ? (v.def.class === 'helicopter' ? 1 : Math.min(1, Math.abs(v.speed) / 15 + 0.4)) : Math.min(1, Math.abs(v.speed) / 6);
  const dir = airborne || v.speed >= 0 ? 1 : -1;
  const grip = slip > 0 ? 1 - 0.35 * slip * Math.min(1, Math.abs(v.speed) / 12) : 1;
  const yank = handbrake && Math.abs(v.speed) > 4 ? 1.8 : 1; // handbrake turn: the rear steps out
  let newAngle = strafe ? v.angle : v.angle + steer * st.turnRate * spdFactor * grip * yank * dir * dt;
  // the tyres (owner, Sep 7: "sports/muscle cars should have enough power to drift under full throttle; squeal and marks"): a human's road car
  // keeps a sideways velocity, `v.lat`. Turning the body swings some of the speed sideways; the tyres pull it back at up to their grip
  // (the content's `stats.grip`, 0–1: TYRE_G × (0.45 + 0.55 grip) m/s²) — less on a wet road, a third with the handbrake, and half under
  // wheelspin: full throttle in a car with the power for it (accel × tuning ≥ SPIN_ACCEL) below 60 % of its top speed. What the tyres
  // cannot hold is a slide: the body goes where the momentum points, the driver counter-steers. `v.skid` is how hard they are losing it.
  let skid = 0;
  if (inp?.player && mode === 'drive' && !airborne) {
    const dA = newAngle - v.angle, wrapA = dA > Math.PI ? dA - Math.PI * 2 : dA < -Math.PI ? dA + Math.PI * 2 : dA;
    let lat = (v.lat ?? 0) - v.speed * Math.sin(wrapA) * SLIDE_K; // (the heading turned right: the momentum now points left of it — a share of it, the arcade steering is far tighter than any tyre)
    const spin = throttle > 0.9 && st.accel * eng >= SPIN_ACCEL && Math.abs(v.speed) < maxF * 0.6 && v.speed >= 0;
    const hold = TYRE_G * (0.45 + 0.55 * (st.grip ?? 0.85)) * (1 - 0.5 * slip) * (handbrake ? 0.33 : 1) * (spin ? 0.6 : 1) * surface;
    // the friction circle: how much of the hold this corner is already asking for, and so how little is left to straighten a slide
    const demand = (Math.abs(v.speed) * Math.abs(wrapA)) / Math.max(dt, 1e-4);
    const spare = hold * (1 - CORNER_USE * Math.min(1, demand / CORNER_REF));
    const pull = Math.min(Math.abs(lat), spare * dt); lat -= Math.sign(lat) * pull;
    if (Math.abs(lat) > 0.3) v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), Math.abs(lat) * 0.25 * dt); // (scrubbing sideways costs speed)
    const cap = Math.abs(v.speed) * (spin ? SPIN_LAT : DRIFT_LAT); // wheels spinning: the tail can come past a drift and all the way round
    lat = Math.max(-cap, Math.min(cap, lat));
    v.lat = Math.abs(lat) < 0.05 ? 0 : lat;
    // what the tyres are not holding turns the car. Past a drift the tail keeps coming — that is the spin — and it follows the
    // steering, so winding it the other way is what catches it, the way it should be.
    const loose = Math.abs(lat) - Math.abs(v.speed) * DRIFT_LAT;
    if (loose > 0 && Math.abs(v.speed) > 3 && steer) newAngle += Math.sign(steer) * Math.min(loose * SPIN_YAW, 3) * dt;
    const locked = (handbrake || (throttle < -0.8 && v.speed > 0)) && Math.abs(v.speed) > 6;
    skid = Math.max(Math.min(1, Math.abs(lat) / 4), spin && Math.abs(v.speed) > 0.5 ? 0.55 : 0, locked ? (handbrake ? 0.8 : 0.5) : 0) * (w.roadAt(v.x, v.y) ? 1 : 0.3);
  }
  v.skid = skid > 0.05 ? skid : undefined;

  // move with collision (try full, then slide on each axis)
  const [fx, fy] = forward(newAngle);
  const lat = strafe || (inp?.player && mode === 'drive' && !airborne) ? v.lat ?? 0 : 0;
  const dx = fx * v.speed * dt + -fy * lat * dt, dy = fy * v.speed * dt + fx * lat * dt; // (a strafe is the heading turned a quarter)
  let hitSpeed = 0, gave = 0;
  // a vehicle already overlapping something (pushed, spawned badly, a corner in the water) may move as
  // long as the move does not make the overlap worse — otherwise it could never get free
  const cur = blockedPoints(w, v, v.x, v.y, v.angle, blockMode);
  const ok = (x: number, y: number, a: number) => blockedPoints(w, v, x, y, a, blockMode, cur + 1) <= cur;
  // a boat under way sheers off the shore (owner: "boats get stuck in shores too easily"): sample the water round the hull
  // and drift away from whatever is not water — a slow sideways set, never enough to fight the helm
  if (mode === 'boat' && Math.abs(v.speed) > 0.3 && !sinking && blockMode !== 'sink') { // (not while running up the beach on purpose, nor once beached: the set would hold the hull at the waterline)
    let px = 0, py = 0; const r = Math.max(v.def.size[0], v.def.size[1]) / 2 + 1.5;
    for (let a = 0; a < 8; a++) { const ang = (a * Math.PI) / 4, sx = v.x + Math.cos(ang) * r, sy = v.y + Math.sin(ang) * r; const t = w.terrainAt(sx, sy); if (!t || !t.boatable || w.blockH(sx, sy) > 0) { px -= Math.cos(ang); py -= Math.sin(ang); } }
    const m = Math.hypot(px, py);
    if (m > 0) { const sx = (px / m) * 2.5 * dt, sy = (py / m) * 2.5 * dt; if (ok(v.x + sx, v.y + sy, v.angle)) { v.x += sx; v.y += sy; } }
  }
  if (ok(v.x + dx, v.y + dy, newAngle)) { v.x += dx; v.y += dy; v.angle = newAngle; }
  else if ((blockMode === 'sink' || blockMode === 'drive') && Math.abs(v.speed) > PROP_BREAK_SPEED && (gave = smashThrough(w, v, v.x + dx, v.y + dy, newAngle, blockMode === 'sink' && Math.abs(v.speed) > RAIL_BREAK_SPEED)) > 0) {
    // through a railing / over a bin, hydrant, bench, bush or cactus: it gives. What it costs the car goes with what it
    // was and how fast it was hit (owner, Sep 10: "the damage they do to cars should be tuned down — no damage for the
    // smallest rammable objects"): the small things (a bin, a bush, a cactus) cost nothing at all; a bigger one — a small
    // rock, a railing — a little paint, and only at speed
    const small = gave <= 1.3, k = Math.min(1, Math.abs(v.speed) / 12);
    v.hitWhat = hitMaterial(w, v, v.x + dx, v.y + dy, newAngle) ?? 'wood'; // (it gave way: whatever it was, it broke)
    v.x += dx; v.y += dy; v.angle = newAngle; v.speed *= small ? 0.96 : 0.85; v.hp -= small ? 0 : Math.round(6 * k); v.bumped = small ? 0.12 : 0.3; hitSpeed = small ? 1.5 : 5 * k;
    if (v.hp <= 0) v.wrecked = true;
  }
  else if (!airborne && slipAlong(w, v, dx, dy, newAngle, ok)) { v.hitWhat = 'scrape'; hitSpeed = Math.min(3, Math.abs(v.speed) * 0.3); v.speed *= 0.9; } // scraping along a wall / tree
  else if (ok(v.x + dx, v.y, v.angle)) { v.hitWhat = hitMaterial(w, v, v.x + dx, v.y + dy, newAngle); v.x += dx; hitSpeed = Math.abs(v.speed) * 0.5; v.speed *= 0.6; }
  else if (ok(v.x, v.y + dy, v.angle)) { v.hitWhat = hitMaterial(w, v, v.x + dx, v.y + dy, newAngle); v.y += dy; hitSpeed = Math.abs(v.speed) * 0.5; v.speed *= 0.6; }
  else {
    v.hitWhat = hitMaterial(w, v, v.x + dx, v.y + dy, newAngle);
    hitSpeed = Math.abs(v.speed); v.speed *= -0.25;
    // ramming a tree or a post: it takes damage too (and may come down)
    if (hitSpeed > 4 && !airborne) for (const [tx, ty] of probePoints(v, v.x + dx, v.y + dy, newAngle)) if (w.tileMaxHp(tx, ty) > 0 && tileBlocked(w, tx, ty, 0, 'drive')) w.damageTile(tx, ty, hitSpeed * 4);
    // wedged against a wall or a tree: let the driver work free — steering turns the car on the spot,
    // throttle nudges it sideways along the obstacle (each only if it does not dig in deeper)
    if (!airborne && inp && Math.abs(v.speed) < 2) {
      if (steer) { const a2 = v.angle + steer * 1.4 * dt; if (ok(v.x, v.y, a2)) v.angle = a2; }
      if (throttle) {
        const [sx, sy] = [-fy, fx]; // sideways
        for (const sgn of [1, -1]) { const nx = v.x + sx * sgn * 0.08, ny = v.y + sy * sgn * 0.08; if (ok(nx, ny, v.angle)) { v.x = nx; v.y = ny; break; } }
      }
    }
  }
  if (!hitSpeed) v.hitWhat = undefined; // (nothing was hit this step: never let the last one colour the next knock)
  if (hitSpeed > 2 && v.lat) v.lat *= 0.2; // (a wall ends a slide)
  if (hitSpeed > 4) {
    v.hp -= hitSpeed * (airborne ? 6 : 1.2);
    v.bumped = 0.3;
    if (v.hp <= 0) v.wrecked = true;
  }
  if (v.angle > Math.PI) v.angle -= Math.PI * 2; else if (v.angle < -Math.PI) v.angle += Math.PI * 2;
  // overpasses: a ground vehicle follows the deck under it (ramps lift it, the far ramp brings it down); off a deck it drops
  if (mode !== 'air' && !sinking) {
    const dh = w.deckH(v.x, v.y);
    if (dh > 0 && v.alt >= dh - 1.0) v.alt = dh;
    else if (v.alt > 0) v.alt = Math.max(0, v.alt - 12 * dt);
  }
  w.setMover(0, 0, 0);
  if (v.bumped > 0) v.bumped -= dt;
  if (v.fireCooldown > 0) v.fireCooldown -= dt;
  return hitSpeed;
}
